import {createFeatureSelector, createSelector} from '@ngrx/store';
import {SchemaEntityState, schemasFeatureKey} from '../_reducer/schema.reducer';
import {DrawSchema} from '../_interface/schema.model';
import * as fieldTypeSelectors from '../../../_selectors/field-type-entity.selectors';
import {FieldTypes} from '../../../_interface/field-types.enum';
import {IFieldType} from '../../../_interface/ifield-type';
import {selectLoggedUser} from 'app/_selectors/user.selectors';
import {LoggedUser} from 'app/_interfaces/logged-user';
import {isEmpty} from 'lodash-es';

export const schemaEntityState = createFeatureSelector<SchemaEntityState>(schemasFeatureKey);
export const selectSchemasLoaded = createSelector(schemaEntityState, ({loaded}) => loaded);
export const selectEditSchema = createSelector(schemaEntityState, ({edit}) => edit);
export const selectAllEntities = createSelector(schemaEntityState, ({entities}) => entities);
export const selectAllList = createSelector(schemaEntityState, ({entities}) => Object.values(entities));
export const selectCountAll = createSelector(schemaEntityState, ({entities}) => Object.values(entities || []).length);
export const selectHasSchemas = createSelector(schemaEntityState, ({entities}) => !!Object.values(entities || []).length);
export const selectOne = (schemaId: string) => createSelector(schemaEntityState, ({entities}): DrawSchema => entities[schemaId]);
export const selectSome = (schemaIds: string[]) => createSelector(schemaEntityState, ({entities}): DrawSchema[] =>
	schemaIds.map(id => entities[id])
);

export const selectOneByName = (schemaName: string) => createSelector(schemaEntityState, ({entities}): DrawSchema =>
	Object.values(entities).find(s => s.name === schemaName)
);

export const selectSchemaFields = createSelector(
	fieldTypeSelectors.selectAll,
	selectAllList,
	selectLoggedUser,
	(types, schemas: DrawSchema[], user: LoggedUser): {fieldType: IFieldType, schema: DrawSchema}[] => {
		const schemaType = types.find(type => type.fieldType === FieldTypes.schema);
		if (!schemaType) {
			console.error('there is no Schema type in fieldTypes');
			return null;
		}
		return schemas
			.map(schema => ({schema, fieldType: schemaType}))
			.filter(schema => schema.schema.professions.includes(+user.profession))
			.filter(schema => !schema.schema.disabled);
	});

export const selectSomeByOrder = (schemaIds: string[]) => createSelector(
	schemaEntityState,
	({entities}): string[] => {
		if (isEmpty(entities)) return [];
		const visibleWhenEmpty = Object.keys(entities)
			.filter((key) => entities[key].visibleWhenEmpty);
		const allEntityIds = [...new Set([...schemaIds, ...visibleWhenEmpty])];
		const enabled = allEntityIds.filter(id => {
			if (!entities[id]) return null;
			return !entities[id].disabled;
		});
		const ordered = enabled.map(id => {
			if (!entities[id]) return null;
			return (entities[id].order !== null) ? entities[id] : null;
		}).filter(Boolean).sort((a, b) => a.order - b.order);
		const nullish = enabled.map(id => {
			if (!entities[id]) return null;
			return (entities[id].order === null) ? entities[id] : null;
		}).filter(Boolean);
		return [...ordered, ...nullish].map(schema => schema.schemaId);
	}
);

export const selectSchemaMode = createSelector(schemaEntityState, state => state.mode);
